export const CALL = 'call';
export const PUT = 'put';
export type OptionType = typeof CALL | typeof PUT;

export const LONG = 'long';
export const SHORT = 'short';
export type OptionPosition = typeof LONG | typeof SHORT;

export const OPTIONS_PER_CONTRACT = 100;
export const YEAR_IN_MS = 1000 * 60 * 60 * 24 * 365.25;

/**
 * At (or within a breath of) expiration the Black-Scholes greeks are singular:
 * gamma and theta carry a 1/√T term that diverges to ±∞ as T→0, and vega
 * collapses to 0. Evaluating the formulas at a meaningless sub-second stub (the
 * old `1e-10`/`1e-8` floors) produced absurd six-figure gammas near the money —
 * or NaN when volatility was 0 — which then rendered as "crazy" or blank greeks
 * as the OPC time slider reached t=0.
 *
 * Once time-to-expiry drops to/under this threshold we instead return the
 * option's mathematical limit at expiry: premium = intrinsic value, delta = a
 * step (±½ exactly at the money), and gamma/theta/vega/rho = 0. `intrinsicOptionValue`
 * and `expiryDelta` below are the shared source of truth used by BOTH the
 * standalone Black-Scholes leg functions and the fast `strategyKernel` so the
 * two paths stay numerically identical (see strategyKernel.test.ts).
 *
 * 1e-6 years ≈ 31.6 seconds: small enough that realistic near-expiry options
 * (even intraday 0DTE with minutes left) still price through real Black-Scholes,
 * large enough that √T can never be numerically degenerate.
 */
export const INTRINSIC_LIMIT_TIME_YEARS = 1e-6;

/** Terminal (at-expiration) value of a single option contract-share. */
export function intrinsicOptionValue(spot: number, strike: number, isCallOption: boolean): number {
  return isCallOption ? Math.max(spot - strike, 0) : Math.max(strike - spot, 0);
}

/**
 * Delta in the T→0 limit: a step function, taken as ±½ exactly at the money
 * (the Black-Scholes limit of N(d1) → ½ as T→0 when spot === strike).
 */
export function expiryDelta(spot: number, strike: number, isCallOption: boolean): number {
  if (spot === strike) return isCallOption ? 0.5 : -0.5;
  if (isCallOption) return spot > strike ? 1 : 0;
  return spot < strike ? -1 : 0;
}

/**
 * Creates a Date object for options expiration at market close (4 PM ET)
 *
 * Options expire at market close (4 PM Eastern Time), not at midnight.
 * This function creates a Date object that represents the correct expiration time,
 * automatically handling daylight saving time transitions.
 *
 * Key benefits:
 * - More accurate time-to-expiration calculations
 * - Consistent behavior across different times of year (DST/Standard)
 * - Eliminates ~16 hour calculation errors from using midnight
 *
 * @param year - Full year (e.g., 2024)
 * @param month - Month (1-12, not 0-11)
 * @param day - Day of month (1-31)
 * @returns Date object set to 4 PM ET on the specified date, converted to UTC
 */
// `new Intl.DateTimeFormat(...)` is expensive (~tens of µs) and was being
// constructed on every Option pricing call — ~15k times per strategy render,
// dominating the chart compute. The formatter depends only on the (constant)
// time zone, so build it once; and since option expirations repeat across
// thousands of calls, memoize the resolved UTC timestamp per calendar date.
const ET_OFFSET_FORMATTER: Intl.DateTimeFormat | null = (() => {
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      timeZoneName: 'longOffset',
    });
  } catch {
    return null;
  }
})();

const marketCloseUtcMsCache = new Map<string, number>();

function computeMarketCloseUtcMs(year: number, month: number, day: number): number {
  try {
    if (ET_OFFSET_FORMATTER) {
      // Check the America/New_York UTC offset for this calendar date (handles DST).
      const baseDate = new Date(year, month - 1, day);
      const parts = ET_OFFSET_FORMATTER.formatToParts(baseDate);
      const offsetPart = parts.find((part) => part.type === 'timeZoneName');

      if (offsetPart?.value) {
        // Parse offset like "GMT-5" or "GMT-4".
        const offsetMatch = offsetPart.value.match(/GMT([+-])(\d+)/);
        if (offsetMatch) {
          const sign = offsetMatch[1] === '+' ? 1 : -1;
          const hours = parseInt(offsetMatch[2], 10);
          const offsetHours = sign * hours;
          const utcHour = 16 - offsetHours; // 4 PM ET -> UTC
          return Date.UTC(year, month - 1, day, utcHour, 0, 0, 0);
        }
      }
    }
    throw new Error('Intl method failed');
  } catch (error) {
    // Fallback for environments without a working Intl offset.
    return createMarketCloseDateFallback(year, month, day).getTime();
  }
}

export function createMarketCloseDate(year: number, month: number, day: number): Date {
  const key = `${year}-${month}-${day}`;
  let utcMs = marketCloseUtcMsCache.get(key);
  if (utcMs === undefined) {
    utcMs = computeMarketCloseUtcMs(year, month, day);
    marketCloseUtcMsCache.set(key, utcMs);
  }
  // Return a fresh Date each call so callers can't mutate the cached value.
  return new Date(utcMs);
}

/**
 * Fallback method for timezone conversion if Intl methods fail
 */
function createMarketCloseDateFallback(year: number, month: number, day: number): Date {
  // Create a temporary date to check if we're in daylight saving time
  const tempDate = new Date(
    `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T12:00:00`,
  );

  // Check if the date is in daylight saving time (EDT) or standard time (EST)
  const isDST =
    tempDate.getTimezoneOffset() <
    Math.max(new Date(year, 0, 1).getTimezoneOffset(), new Date(year, 11, 31).getTimezoneOffset());

  // EDT is UTC-4, EST is UTC-5
  const etOffset = isDST ? -4 : -5;
  const utcHour = 16 - etOffset; // 20 for EST, 21 for EDT

  return new Date(Date.UTC(year, month - 1, day, utcHour, 0, 0, 0));
}

/**
 * Converts a date string (e.g., "2024-01-15") to a market close Date object
 * Useful for handling URL parameters and user inputs
 */
export function dateStringToMarketClose(dateString: string): Date {
  // Parse the date string manually to avoid timezone issues
  const parts = dateString.split('-');
  if (parts.length !== 3) {
    throw new Error(`Invalid date string: ${dateString}`);
  }

  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10);
  const day = parseInt(parts[2], 10);

  // Validate the parsed values
  if (
    isNaN(year) ||
    isNaN(month) ||
    isNaN(day) ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > 31 ||
    year < 1900 ||
    year > 2100
  ) {
    throw new Error(`Invalid date string: ${dateString}`);
  }

  return createMarketCloseDate(year, month, day);
}

/**
 * Updated expirationToDate function that uses market close time
 * Legacy wrapper around createMarketCloseDate for backward compatibility
 */
export function expirationToDate(ey: number, em: number, ed: number): Date {
  return createMarketCloseDate(ey, em, ed);
}

export interface UnderlyingStockPosition {
  price: number;
  quantity: number;
}

export interface OptionInputParams {
  type: OptionType;
  spot: number;
  strike: number;
  premium?: number | null;
  timeToExpirationInYears?: number | null;
  riskFreeRate: number;
  volatility: number;
  position: OptionPosition;
  numberOfContracts?: number;

  // Expiration as "YYYY-MM-DD" string (avoids Date parsing issues)
  expirationDateString?: string;

  dateAtCalculation?: Date;
}

export interface OptionsParams extends OptionInputParams {
  timeToExpirationInYears: number;
  calculatedPremium?: number;
  isUsingCalculatedPremium?: boolean;
  premium: number;
  isCall: boolean;
  isPut: boolean;
  isLong: boolean;
  isShort: boolean;
}

export interface OptionPayoffParams extends OptionsParams {
  position: OptionPosition;
  premium: number;
}

export interface ImpliedVolatilityBisectParams extends OptionsParams {
  premium: number;
  maxIterations?: number;
  precision?: number;
}

export function isCall(type: OptionType): boolean {
  return type === CALL;
}

export function isPut(type: OptionType): boolean {
  return type === PUT;
}

export function isLong(numberOfContracts: number): boolean {
  return numberOfContracts > 0;
}

export function isShort(numberOfContracts: number): boolean {
  return numberOfContracts < 0;
}

export function erf(x: number): number {
  const sign = x < 0 ? -1 : 1;
  x = Math.abs(x);

  const a1 = 0.254829592;
  const a2 = -0.284496736;
  const a3 = 1.421413741;
  const a4 = -1.453152027;
  const a5 = 1.061405429;
  const p = 0.3275911;

  const t = 1.0 / (1.0 + p * x);
  const y = ((((a5 * t + a4) * t + a3) * t + a2) * t + a1) * t;

  return sign * (1 - y * Math.exp(-x * x));
}

export function normcdf(x: number): number {
  return (1.0 + erf(x / Math.sqrt(2.0))) / 2.0;
}

export function normpdf(x: number): number {
  return Math.exp((-x * x) / 2) / Math.sqrt(2 * Math.PI);
}
